import React, { useState } from 'react'
import { Box, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material'
import SymbolIcon from '../../../../assets/icons/SymbolIcon'
import StatusLineIcon from '../../../../assets/icons/StatusLineSymbol'
import CanvasIcon from '../../../../assets/icons/CanvasIcon'
import OiStatusLineSettings from './OiStatusLineSettings'
import OiSymbolSettings from './OiSymbolSettings'
import MultiStrikeCanvasSettings from '../../../multiStikeChart/chartWrapper/chartMetadata/MultiStrikeCanvasSettings'
import { useWidgetSettingsSelectMenuProps } from '../../widgetSettingsMenuContext'
import {
    chartSettingsGridRowSx,
    chartSettingsLabelSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionTitleSx,
    chartSettingsTabPanelSx,
} from '../../../chartSettings/chartSettingsLayout'

type Props = {
    viewKey: string
    expiryMenu: {
        value: string
        label: string
    }[]
    selectedExpiry: {
        expirySeconds: string
        expiry: string
    } | undefined
    onExpiryChange: (expiry: { expirySeconds: string; expiry: string }) => void
}

const tabStyle = {
    padding: '6px 0px 6px 20px',
    alignItems: 'start',
    justifyContent: 'center',
    textTransform: 'none',
    color: (theme: any) => theme.palette.text.primary,
    borderRadius: '4px',
    '&.Mui-selected': {
        color: (theme: any) => theme.palette.text.primary,
        backgroundColor: (theme: any) => theme.palette.action.selected,
        '& .tab-label': {
            fontWeight: 600,
        },
    },
} as const

const OiSettingsTabs = ({ viewKey, expiryMenu, selectedExpiry, onExpiryChange }: Props) => {
    const [tab, setTab] = useState(0)
    const selectMenuProps = useWidgetSettingsSelectMenuProps()

    return (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 320 }}>
            <Tabs
                orientation="vertical"
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                sx={{
                    borderRight: 1,
                    borderColor: 'divider',
                    minWidth: 200,
                    paddingTop: '6px',
                }}
            >
                <Tab
                    value={0}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SymbolIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Symbol
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={1}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SymbolIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Inputs
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={2}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <StatusLineIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Status Line
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={3}
                    sx={tabStyle}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CanvasIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Canvas
                            </Typography>
                        </span>
                    }
                />
            </Tabs>

            <Box sx={{ flex: 1, padding: 2, minWidth: 0 }}>
                <Box sx={{ display: 'grid', width: '100%', '& > *': { gridArea: '1 / 1' } }}>
                    <Box sx={{ visibility: tab === 0 ? 'visible' : 'hidden', width: '100%', ...chartSettingsTabPanelSx }}>
                        <OiSymbolSettings viewKey={viewKey} />
                    </Box>
                    <Box sx={{ visibility: tab === 1 ? 'visible' : 'hidden', width: '100%', ...chartSettingsTabPanelSx }}>
                        <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                            SELECTION
                        </Typography>
                        <Box sx={{ ...chartSettingsGridRowSx, width: '100%' }}>
                            <Typography sx={chartSettingsLabelSx}>Expiry</Typography>
                            <Box sx={{ ...chartSettingsRowControlsSx, width: '100%' }}>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={selectedExpiry?.expirySeconds ?? ''}
                                    MenuProps={selectMenuProps}
                                    onChange={(e) => {
                                        const menuItem = expiryMenu.find((item) => item.value === e.target.value)
                                        if (menuItem) {
                                            onExpiryChange({
                                                expiry: menuItem.label,
                                                expirySeconds: menuItem.value,
                                            })
                                        }
                                    }}
                                >
                                    {expiryMenu.map((expiry) => (
                                        <MenuItem key={expiry.value} value={expiry.value}>
                                            {expiry.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ visibility: tab === 2 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <OiStatusLineSettings viewKey={viewKey} />
                    </Box>
                    <Box sx={{ visibility: tab === 3 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>
                        <MultiStrikeCanvasSettings viewKey={viewKey} />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default OiSettingsTabs
